/**
 * @quotech/ui - Universal Appearance Settings Tab
 *
 * PATH: packages/ui/src/components/settings/AppearanceTab.tsx
 * PURPOSE: Theme and appearance preferences across all hubs (orchestrator)
 * WHY: Supports light/dark/system themes and accent color selection.
 *      Hub-agnostic — uses localStorage for persistence by default.
 *
 * FLOW:
 * ┌───────────────┐    ┌──────────────────┐    ┌──────────────────┐
 * │ user prefs    │ →  │ theme + display   │ →  │ onChange() + DOM  │
 * └───────────────┘    └──────────────────┘    └──────────────────┘
 *
 * DEPENDENCIES:
 *  - ThemeSelector: Light/Dark/System option cards
 *  - DensitySelector: Compact mode + reduce motion toggles
 */

import { useState, useEffect, useCallback } from 'react'
import { Palette, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import { ThemeSelector } from './appearance-tab/ThemeSelector'
import { DensitySelector } from './appearance-tab/DensitySelector'

// =============================================================================
// TYPES
// =============================================================================

export type ThemeMode = 'light' | 'dark' | 'system'

export interface AccentColor {
  name: string
  value: string
  bgClass: string
  textClass: string
}

export interface AppearancePreferences {
  theme: ThemeMode
  accentColor: string
  compactMode?: boolean
  reduceMotion?: boolean
}

export interface AppearanceTabProps {
  /** Current preferences */
  preferences?: Partial<AppearancePreferences>
  /** Callback when preferences change */
  onChange?: (prefs: AppearancePreferences) => void
  /** Storage key for localStorage persistence */
  storageKey?: string
  /** Available accent colors */
  accentColors?: AccentColor[]
  /** Custom className */
  className?: string
}

// =============================================================================
// DEFAULT ACCENT COLORS
// =============================================================================

const DEFAULT_ACCENT_COLORS: AccentColor[] = [
  { name: 'Teal', value: 'teal', bgClass: 'bg-teal-500', textClass: 'text-teal-500' },
  { name: 'Blue', value: 'blue', bgClass: 'bg-blue-500', textClass: 'text-blue-500' },
  { name: 'Purple', value: 'purple', bgClass: 'bg-purple-500', textClass: 'text-purple-500' },
  { name: 'Pink', value: 'pink', bgClass: 'bg-pink-500', textClass: 'text-pink-500' },
  { name: 'Orange', value: 'orange', bgClass: 'bg-orange-500', textClass: 'text-orange-500' },
  { name: 'Emerald', value: 'emerald', bgClass: 'bg-emerald-500', textClass: 'text-emerald-500' },
  { name: 'Indigo', value: 'indigo', bgClass: 'bg-indigo-500', textClass: 'text-indigo-500' },
  { name: 'Rose', value: 'rose', bgClass: 'bg-rose-500', textClass: 'text-rose-500' },
]

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function AppearanceTab({
  preferences: initialPrefs,
  onChange,
  storageKey = 'quotech-appearance',
  accentColors = DEFAULT_ACCENT_COLORS,
  className,
}: AppearanceTabProps) {
  const [prefs, setPrefs] = useState<AppearancePreferences>(() => {
    if (initialPrefs?.theme && initialPrefs?.accentColor) {
      return initialPrefs as AppearancePreferences
    }

    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        return {
          theme: parsed.theme || 'system',
          accentColor: parsed.accentColor || 'teal',
          compactMode: parsed.compactMode || false,
          reduceMotion: parsed.reduceMotion || false,
        }
      }
    } catch {
      // Ignore localStorage errors
    }

    return {
      theme: 'system',
      accentColor: 'teal',
      compactMode: false,
      reduceMotion: false,
    }
  })

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement

    if (prefs.theme === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.classList.toggle('dark', isDark)
    } else {
      root.classList.toggle('dark', prefs.theme === 'dark')
    }

    if (prefs.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        root.classList.toggle('dark', e.matches)
      }
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
  }, [prefs.theme])

  const updatePrefs = useCallback((updates: Partial<AppearancePreferences>) => {
    setPrefs(prev => {
      const next = { ...prev, ...updates }

      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // Ignore
      }

      onChange?.(next)
      return next
    })
  }, [storageKey, onChange])

  return (
    <div className={cn('space-y-8', className)}>
      {/* Theme Section */}
      <ThemeSelector
        currentTheme={prefs.theme}
        onThemeChange={(theme) => updatePrefs({ theme })}
      />

      {/* Accent Color Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Accent Color
          </h3>
        </div>
        <div className="flex flex-wrap gap-3">
          {accentColors.map((color) => (
            <button
              key={color.value}
              onClick={() => updatePrefs({ accentColor: color.value })}
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                color.bgClass,
                prefs.accentColor === color.value
                  ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-slate-900 dark:ring-white scale-110'
                  : 'hover:scale-105'
              )}
              title={color.name}
            >
              {prefs.accentColor === color.value && (
                <Check className="w-5 h-5 text-white" />
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
          Selected: {accentColors.find(c => c.value === prefs.accentColor)?.name || prefs.accentColor}
        </p>
      </div>

      {/* Display Options */}
      <DensitySelector
        compactMode={prefs.compactMode}
        reduceMotion={prefs.reduceMotion}
        onToggleCompact={() => updatePrefs({ compactMode: !prefs.compactMode })}
        onToggleMotion={() => updatePrefs({ reduceMotion: !prefs.reduceMotion })}
      />
    </div>
  )
}

export default AppearanceTab
